import { NonEmptyArray } from 'fp-ts/NonEmptyArray';
import { Icon, IconName, Stack, Title } from '@optimus-web/core';
import { lt } from '@mews/framework';
import { ModalSidePanel, TabbedContent } from '@mews-commander/ui';
import { CancellationPolicyName } from '../../../components';
import { useCancellationPolicyGetQuery } from '../queries';
import { CancellationPolicyActions } from './CancellationPolicyActions';
import { CancellationPolicyDetailsTabContent } from './CancellationPolicyDetailsTabContent';
import { PolicyAssignedRateGroupsTabContent } from './PolicyAssignedRateGroupsTabContent';

export type CancellationPoliciesTab = 'details' | 'rateGroups';

const CANCELLATION_POLICIES_SIDE_PANEL_TABS: NonEmptyArray<CancellationPoliciesTab> = ['details', 'rateGroups'];

interface Props {
    cancellationPolicyId?: string;
    handleCloseSidePanel: () => void;
}

export const CancellationPolicySidePanel = ({ cancellationPolicyId, handleCloseSidePanel }: Props) => {
    const { isFetching, data } = useCancellationPolicyGetQuery(cancellationPolicyId);

    return (
        <ModalSidePanel
            opened={!!cancellationPolicyId}
            title={
                data && (
                    <Stack itemAlignment="center" spacing="100">
                        <Title level={2} wordBreak="break-all" ellipsis maxLines={2}>
                            <CancellationPolicyName {...data} />
                        </Title>
                        {data.isPortfolioManaged ? <Icon value={IconName.CustomLink} /> : null}
                    </Stack>
                )
            }
            onClose={handleCloseSidePanel}
            content={
                data && (
                    <TabbedContent
                        inline
                        showDivider
                        tabs={CANCELLATION_POLICIES_SIDE_PANEL_TABS}
                        tabLabels={{
                            details: lt('DetailPlural'),
                            rateGroups: lt('RateGroupPlural'),
                        }}
                    >
                        {({ matchTabs }) =>
                            matchTabs({
                                details: () => (
                                    <CancellationPolicyDetailsTabContent
                                        {...data}
                                        key={undefined}
                                        policyKey={data?.key}
                                    />
                                ),
                                rateGroups: () => (
                                    <PolicyAssignedRateGroupsTabContent rateGroups={data.rateGroups || []} />
                                ),
                            })
                        }
                    </TabbedContent>
                )
            }
            width="medium"
            loading={isFetching}
            headerActions={
                cancellationPolicyId && data ? (
                    <CancellationPolicyActions
                        isEditable={!data.isPortfolioManaged}
                        onSidePanelClose={handleCloseSidePanel}
                        cancellationPolicyId={cancellationPolicyId}
                    />
                ) : null
            }
        />
    );
};
